import React, { useState, useEffect, useRef } from "react";
import { Select } from "antd";
import { getRooms } from "../../../../apis/room";
import { Visit } from "../../../../interfaces/Visit";
import { useNotification } from "../../../../NotificationContext";
import { updateVisit } from "../../../../apis/visit";
import { useAppDispatch } from "../../../../hooks";
import { changeVisit } from "../../../../features/visit/visitSlice";

const { Option: SelectOption } = Select;

interface RoomTransferFormProps {
  visit: Visit;
  initialData?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

interface Room {
  id: number;
  name: string;
  floor: string;
  status: string;
}

const RoomTransferForm: React.FC<RoomTransferFormProps> = ({
  visit,
  initialData,
  onSave,
  onCancel,
}) => {
  const { showNotification } = useNotification();
  const dispatch = useAppDispatch();
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentRoom, setCurrentRoom] = useState<Room | null>(null);
  const isSubmittingRef = useRef(false);
  
  const [formData, setFormData] = useState({
    fromRoomId: initialData?.fromRoomId || visit?.roomId || null,
    toRoomId: initialData?.toRoomId || null,
    reason: initialData?.reason || "",
  });

  useEffect(() => {
    fetchCurrentRoomAndAvailableRooms();
  }, []);

  const fetchCurrentRoomAndAvailableRooms = async () => {
    const currentRoomId = visit?.roomId;
    
    try {
      setLoading(true);
      // Fetch all rooms to get current room info and available rooms
      const res: any = await getRooms({ per_page: 1000 });
      const allRooms: Room[] = res.data || [];
      
      // Find and set current room
      if (currentRoomId) {
        const room = allRooms.find((r) => r.id === currentRoomId);
        if (room) {
          setCurrentRoom(room);
          setFormData((prev) => ({
            ...prev,
            fromRoomId: currentRoomId,
          }));
        }
      }
      
      // Filter available rooms and exclude current room
      const available = allRooms.filter(
        (room) => room.status === "available" && room.id !== currentRoomId
      );
      setAvailableRooms(available);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
      showNotification(
        "error",
        "Failed to load rooms",
        "",
        "topRight"
      );
      setAvailableRooms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent multiple submissions
    if (isSubmittingRef.current || saving) {
      return;
    }
    
    if (!formData.toRoomId) {
      showNotification(
        "error",
        "Please select a destination room",
        "",
        "topRight"
      );
      return;
    }
    if (formData.fromRoomId === formData.toRoomId) {
      showNotification(
        "error",
        "Destination room must be different from current room",
        "",
        "topRight"
      );
      return;
    }

    try {
      isSubmittingRef.current = true;
      setSaving(true);
      // Update visit with new roomId
      const updatedVisit = {
        ...visit,
        roomId: formData.toRoomId,
      };

      const res = await updateVisit(updatedVisit) as Visit;
      dispatch(changeVisit(res));
      
      // Call onSave with the transfer data - parent IPDEntryForm will create the visit entry
      // Only call onSave once
      if (isSubmittingRef.current) {
        onSave({
          ...formData,
          transferDate: new Date().toISOString(),
        });
      }
    } catch (error: any) {
      console.error("Failed to update room:", error);
      const errorMessage = error?.response?.data?.message || "Failed to update room";
      showNotification(
        "error",
        errorMessage,
        "",
        "topRight"
      );
    } finally {
      isSubmittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          From Room
        </label>
        <input
          type="text"
          value={currentRoom ? `${currentRoom.name}${currentRoom.floor ? ` (Floor ${currentRoom.floor})` : ""}` : "No room assigned"}
          disabled
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 cursor-not-allowed sm:text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          To Room <span className="text-red-500">*</span>
        </label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select destination room"
          value={formData.toRoomId}
          onChange={(value) =>
            setFormData({ ...formData, toRoomId: value })
          }
          loading={loading}
          showSearch
          filterOption={(input, option) => {
            const children = option?.children;
            const text = typeof children === 'string' ? children : String(children);
            return text.toLowerCase().includes(input.toLowerCase());
          }}
        >
          {availableRooms.map((room) => (
            <SelectOption key={room.id} value={room.id}>
              {room.name}{room.floor ? ` (Floor ${room.floor})` : ""}
            </SelectOption>
          ))}
        </Select>
        {availableRooms.length === 0 && !loading && (
          <p className="mt-1 text-sm text-gray-500">
            No available rooms found
          </p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reason for Transfer
        </label>
        <textarea
          value={formData.reason}
          onChange={(e) =>
            setFormData({ ...formData, reason: e.target.value })
          }
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
          placeholder="Reason for room transfer..."
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save Transfer"}
        </button>
      </div>
    </form>
  );
};

export default RoomTransferForm;


